import { useContext } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import cookies from 'js-cookie';

import { login } from '@/api/auth';
import { RequestService } from '@/api/services/request.service';
import { REFRESH_TOKEN_NAME, TOKEN_COOKIE_NAME } from '@/auth/constants';
import { REDIRECT_TO_QUERY } from '@/constants/authorization';
import {
	MAIN_URL,
	MAKE_LOGIN_URL,
	PERSONAL_ACCOUNT_URL,
	STORAGE_ACCOUNT_URL,
} from '@/constants/routes';
import { FeedbackContext } from '@/context/feeadback.context';
import { useUserActions } from '@/context/user.context';
import { TranslationNamespace } from '@/i18n';
import { Credentials } from '@/types/api/auth';
import { CompanyType } from '@/types/common-entities';

type Props = {
	redirectOnError?: boolean;
	redirectOnSuccess?: boolean;
};

export const useLoginUser = ({ redirectOnError, redirectOnSuccess }: Props = {}) => {
	const router = useRouter();
	const { message } = useContext(FeedbackContext);
	const { updateUser } = useUserActions();
	const t = useTranslation(TranslationNamespace.LoginRegistration).t;

	const loginUser = async ({ email, password }: Credentials): Promise<void> => {
		try {
			const { token, companyType, refresh } = await login({
				email,
				password,
			});

			cookies.set(TOKEN_COOKIE_NAME, token);
			cookies.set(REFRESH_TOKEN_NAME, refresh);
			RequestService.setAuthHeader(token);

			await updateUser();

			if (!redirectOnSuccess) {
				return;
			}
			const redirectUrl = router.query[REDIRECT_TO_QUERY];
			if (typeof redirectUrl === 'string' && redirectUrl.startsWith('/')) {
				return void router.push(redirectUrl, redirectUrl, {
					locale: router.locale,
					shallow: true,
				});
			}
			if (companyType === CompanyType.Warehouse) {
				return void router.push(STORAGE_ACCOUNT_URL, STORAGE_ACCOUNT_URL, {
					locale: router.locale,
					shallow: true,
				});
			} else if (companyType === CompanyType.FishCompany) {
				return void router.push(PERSONAL_ACCOUNT_URL, PERSONAL_ACCOUNT_URL, {
					locale: router.locale,
					shallow: true,
				});
			} else {
				return void router.push(MAIN_URL, MAIN_URL, {
					locale: router.locale,
					shallow: true,
				});
			}
		} catch (e) {
			message.error(t('feedback.try-again-later'));
			if (redirectOnError) {
				const url = `${MAKE_LOGIN_URL()}?${new URLSearchParams(router.query as Record<any, any>).toString()}`;
				void router.push(url, url, { locale: router.locale });
			} else {
				throw e;
			}
		}
	};

	return {
		loginUser,
	};
};
